import React, { useEffect, useRef } from 'react'

import { ButtonBase, Chip, IconButton, useTheme } from '@material-ui/core'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import ApartmentIcon from '@material-ui/icons/Apartment'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import FolderIcon from '@material-ui/icons/Folder'
import PeopleIcon from '@material-ui/icons/People'

import { typographyStyle } from '../../theme/typographyStyle'
import { RecordsPivot } from './types'
import { RecordsPersonRow, RecordsUnitRow } from '../../hooks/useRecords'
import { PEOPLE_COHORTS, RecordsPeopleCohort } from './paths'

const COHORT_LABEL: Record<RecordsPeopleCohort, string> = {
    current: 'Current',
    future: 'Future',
    past: 'Past',
    cancelled: 'Cancelled',
    incoming: 'Incoming',
}

const personKey = (person: RecordsPersonRow) =>
    person.user_id != null ? `user-${person.user_id}` : `lead-${person.lead_id}`

const personStatusLabel = (status: string) => {
    if (status === 'former') return 'Past'
    return COHORT_LABEL[status as RecordsPeopleCohort] || status
}

interface RecordsIndexProps {
    pivot: RecordsPivot
    units: RecordsUnitRow[]
    unitsLoading: boolean
    people: RecordsPersonRow[]
    peopleLoading: boolean
    searchingPeople: boolean
    selectedUnitId: number | null
    selectedUserId: number | null
    selectedLeadId: number | null
    onSelectAll: () => void
    onSelectUnit: (unitId: number) => void
    onSelectPerson: (person: RecordsPersonRow) => void
    suggestedPeople?: RecordsPersonRow[]
    suggestedPeopleLoading?: boolean
    cohort?: string | null
    onSelectCohort?: (cohort: RecordsPeopleCohort) => void
    peopleCount?: number
    peoplePage?: number
    peoplePageCount?: number
    peoplePageSize?: number
    peopleTruncated?: boolean
    onSelectPage?: (page: number) => void
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        sidebar: {
            width: 240,
            minWidth: 240,
            maxWidth: 240,
            borderRight: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
        },
        header: {
            padding: theme.spacing(2.5, 3),
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
        },
        headerTop: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
        },
        headerIcon: {
            width: 36,
            height: 36,
            borderRadius: theme.spacing(1),
            backgroundColor: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        cohortChips: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(0.5),
            marginTop: theme.spacing(1.5),
        },
        headerTitle: {
            ...(typographyStyle(theme.typography.subtitle1) as Record<
                string,
                unknown
            >),
            fontWeight: 700,
            color: theme.palette.text.primary,
        },
        scrollContainer: {
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: theme.spacing(1, 0),
        },
        row: {
            width: 'calc(100% - 16px)',
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            padding: theme.spacing(1.25, 1.5),
            borderRadius: theme.spacing(1),
            margin: theme.spacing(0, 1, 0.5),
            textAlign: 'left',
            border: '1px solid transparent',
            '&:hover': {
                backgroundColor: theme.palette.grey[50],
                borderColor: theme.palette.grey[200],
            },
        },
        rowSelected: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
            '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                borderColor: 'transparent',
            },
        },
        rowLabel: {
            ...(typographyStyle(theme.typography.body2) as Record<
                string,
                unknown
            >),
            fontWeight: 600,
            minWidth: 0,
            flex: 1,
        },
        badge: {
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            fontWeight: 700,
            minWidth: 20,
            textAlign: 'center',
            flexShrink: 0,
        },
        folderPath: {
            display: 'block',
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            opacity: 0.75,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        empty: {
            padding: theme.spacing(3, 2),
            textAlign: 'center',
            color: theme.palette.text.secondary,
            ...(typographyStyle(theme.typography.body2) as Record<
                string,
                unknown
            >),
        },
        sectionLabel: {
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
            padding: theme.spacing(1.5, 2.5, 0.5),
        },
        hint: {
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.secondary,
            padding: theme.spacing(1, 2.5, 1.5),
        },
        pager: {
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(0.5),
            padding: theme.spacing(0.5, 1, 0.5, 1.5),
            borderTop: `1px solid ${theme.palette.divider}`,
        },
        pagerLabel: {
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.secondary,
            minWidth: 0,
        },
        pagerButtons: {
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
        },
    }),
)

export const RecordsIndex = ({
    pivot,
    units,
    unitsLoading,
    people,
    peopleLoading,
    searchingPeople,
    selectedUnitId,
    selectedUserId,
    selectedLeadId,
    onSelectAll,
    onSelectUnit,
    onSelectPerson,
    suggestedPeople = [],
    suggestedPeopleLoading = false,
    cohort = 'current',
    onSelectCohort,
    peopleCount = 0,
    peoplePage = 1,
    peoplePageCount = 1,
    peoplePageSize = 50,
    peopleTruncated = false,
    onSelectPage,
}: RecordsIndexProps) => {
    const theme = useTheme()
    const classes = useStyles()
    const scrollRef = useRef<HTMLDivElement>(null)
    const allSelected =
        pivot === 'units'
            ? selectedUnitId == null
            : selectedUserId == null && selectedLeadId == null
    const HeaderIcon = pivot === 'units' ? ApartmentIcon : PeopleIcon
    const activeCohort = (cohort || 'current') as RecordsPeopleCohort
    const showPager =
        pivot === 'people' &&
        !searchingPeople &&
        peopleCount > peoplePageSize &&
        Boolean(onSelectPage)
    const rangeStart =
        peopleCount === 0 ? 0 : (peoplePage - 1) * peoplePageSize + 1
    const rangeEnd = Math.min(peoplePage * peoplePageSize, peopleCount)
    const refineHint = peopleTruncated
        ? `Showing ${people.length} matches — refine search`
        : null

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0
    }, [peoplePage, searchingPeople, activeCohort])

    return (
        <aside className={classes.sidebar}>
            <div className={classes.header}>
                <div className={classes.headerTop}>
                    <div className={classes.headerIcon}>
                        <HeaderIcon
                            style={{
                                color: theme.palette.common.white,
                                fontSize: 20,
                            }}
                        />
                    </div>
                    <span className={classes.headerTitle}>
                        {pivot === 'units' ? 'Units' : 'People'}
                    </span>
                </div>
                {pivot === 'people' && onSelectCohort ? (
                    <div className={classes.cohortChips}>
                        {PEOPLE_COHORTS.map((item) => (
                            <Chip
                                key={item}
                                size="small"
                                clickable
                                color={
                                    activeCohort === item
                                        ? 'primary'
                                        : 'default'
                                }
                                label={COHORT_LABEL[item]}
                                onClick={() => onSelectCohort(item)}
                                style={{ fontWeight: 600 }}
                            />
                        ))}
                    </div>
                ) : null}
            </div>
            <div className={classes.scrollContainer} ref={scrollRef}>
                <ButtonBase
                    onClick={onSelectAll}
                    className={`${classes.row} ${
                        allSelected ? classes.rowSelected : ''
                    }`}
                >
                    <span className={classes.rowLabel}>
                        {pivot === 'units' ? 'All units' : 'All people'}
                    </span>
                </ButtonBase>
                {pivot === 'people' ? (
                    peopleLoading ? (
                        <div className={classes.empty}>Loading people…</div>
                    ) : people.length === 0 ? (
                        <div className={classes.empty}>
                            {searchingPeople
                                ? 'No people match that search.'
                                : `No ${COHORT_LABEL[
                                      activeCohort
                                  ].toLowerCase()} people. Search to find someone else.`}
                        </div>
                    ) : (
                        <>
                            {people.map((person) => {
                                const isSelected =
                                    (person.user_id != null &&
                                        selectedUserId === person.user_id) ||
                                    (person.user_id == null &&
                                        person.lead_id != null &&
                                        selectedLeadId === person.lead_id)
                                return (
                                    <ButtonBase
                                        key={personKey(person)}
                                        onClick={() => onSelectPerson(person)}
                                        className={`${classes.row} ${
                                            isSelected
                                                ? classes.rowSelected
                                                : ''
                                        }`}
                                    >
                                        <span className={classes.rowLabel}>
                                            <span
                                                className={classes.folderPath}
                                            >
                                                {person.unit_name
                                                    ? `${personStatusLabel(
                                                          person.status,
                                                      )} · ${person.unit_name}`
                                                    : personStatusLabel(
                                                          person.status,
                                                      )}
                                            </span>
                                            {person.name ||
                                                person.email ||
                                                'Person'}
                                        </span>
                                    </ButtonBase>
                                )
                            })}
                            {refineHint ? (
                                <div className={classes.hint}>{refineHint}</div>
                            ) : null}
                        </>
                    )
                ) : unitsLoading ? (
                    <div className={classes.empty}>Loading units…</div>
                ) : (
                    <>
                        {units.length === 0 ? (
                            <div className={classes.empty}>
                                {suggestedPeople.length > 0 ||
                                suggestedPeopleLoading
                                    ? 'No units match. People who match are below.'
                                    : 'No units match.'}
                            </div>
                        ) : (
                            units.map((unit) => {
                                const isSelected = selectedUnitId === unit.id
                                return (
                                    <ButtonBase
                                        key={unit.id}
                                        onClick={() => onSelectUnit(unit.id)}
                                        className={`${classes.row} ${
                                            isSelected
                                                ? classes.rowSelected
                                                : ''
                                        }`}
                                    >
                                        <FolderIcon
                                            style={{
                                                fontSize: 16,
                                                color: 'inherit',
                                            }}
                                        />
                                        <span className={classes.rowLabel}>
                                            {unit.folder_path ? (
                                                <span
                                                    className={
                                                        classes.folderPath
                                                    }
                                                >
                                                    {unit.folder_path}
                                                </span>
                                            ) : null}
                                            {unit.name}
                                        </span>
                                        {unit.open_service_request_count > 0 ? (
                                            <span className={classes.badge}>
                                                {
                                                    unit.open_service_request_count
                                                }
                                            </span>
                                        ) : null}
                                    </ButtonBase>
                                )
                            })
                        )}
                        {suggestedPeopleLoading ? (
                            <div className={classes.empty}>
                                Looking up people…
                            </div>
                        ) : suggestedPeople.length > 0 ? (
                            <>
                                <div className={classes.sectionLabel}>
                                    People
                                </div>
                                {suggestedPeople.map((person) => {
                                    const isSelected =
                                        (person.user_id != null &&
                                            selectedUserId ===
                                                person.user_id) ||
                                        (person.user_id == null &&
                                            person.lead_id != null &&
                                            selectedLeadId === person.lead_id)
                                    return (
                                        <ButtonBase
                                            key={`suggest-${personKey(person)}`}
                                            onClick={() =>
                                                onSelectPerson(person)
                                            }
                                            className={`${classes.row} ${
                                                isSelected
                                                    ? classes.rowSelected
                                                    : ''
                                            }`}
                                        >
                                            <PeopleIcon
                                                style={{
                                                    fontSize: 16,
                                                    color: 'inherit',
                                                }}
                                            />
                                            <span className={classes.rowLabel}>
                                                {person.unit_name ? (
                                                    <span
                                                        className={
                                                            classes.folderPath
                                                        }
                                                    >
                                                        {person.unit_name}
                                                    </span>
                                                ) : null}
                                                {person.name ||
                                                    person.email ||
                                                    'Person'}
                                            </span>
                                        </ButtonBase>
                                    )
                                })}
                                {refineHint ? (
                                    <div className={classes.hint}>
                                        {refineHint}
                                    </div>
                                ) : null}
                            </>
                        ) : null}
                    </>
                )}
            </div>
            {showPager ? (
                <div className={classes.pager}>
                    <span className={classes.pagerLabel}>
                        {rangeStart}–{rangeEnd} of {peopleCount}
                    </span>
                    <div className={classes.pagerButtons}>
                        <IconButton
                            size="small"
                            aria-label="Previous page"
                            disabled={peoplePage <= 1}
                            onClick={() => onSelectPage?.(peoplePage - 1)}
                        >
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            aria-label="Next page"
                            disabled={peoplePage >= peoplePageCount}
                            onClick={() => onSelectPage?.(peoplePage + 1)}
                        >
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </div>
                </div>
            ) : null}
        </aside>
    )
}
